import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { ConfigService, DEFAULT_CONFIG, HeaderTemplate, SystemService } from 'libs/shared/src';
import { HeaderBasicComponent } from './basic/header-basic.component';
import { HeaderSlimComponent } from './slim/header-slim.component';
import { HeaderService } from './services/header.service';

@Component({
  selector: 'gs-header',
  imports: [HeaderBasicComponent, HeaderSlimComponent],
  host: {
    '[class.shadow-md]': 'showShadow()',
    '[style.position]': "isSticky() ? 'sticky' : null",
    '[style.top.px]': 'stickyTopOffset()'
  },
  template: `
    @let template = activeTemplate();
    @if (template) {
      @switch (template) {
        @case ('basic') {
          <header-basic [scrollPosition]="scrollPosition()"></header-basic>
        }
        @case ('slim') {
          <header-slim [scrollPosition]="scrollPosition()"></header-slim>
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      display: block;
    }
  `
})
export class HeaderComponent {
  // Inputs
  /** Scroll position on main page */
  scrollPosition: InputSignal<number> = input.required<number>();

  // Services
  private readonly configService = inject(ConfigService);
  protected readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly system = inject(SystemService);

  // Properties & Signals
  /** The current active header template */
  protected readonly activeTemplate: Signal<HeaderTemplate> = computed(() => {
    return this.configService.config()?.header?.template ?? 'basic';
  });
  /** Whether the header drop shadow is enabled in the config. Applied as a host class. */
  protected readonly showShadow: Signal<boolean> = computed(() => {
    return this.configService.config()?.header?.showShadow ?? DEFAULT_CONFIG.header?.showShadow ?? false;
  });
  /** Whether the header should stick to the top of the viewport when scrolling. */
  protected readonly isSticky: Signal<boolean> = computed(() => {
    return this.configService.config()?.header?.sticky ?? DEFAULT_CONFIG.header?.sticky ?? false;
  });
  /**
   * Whether the topbar stays pinned with the sticky header. When false, only the main header stays.
   * The backend delivers a never-saved value as `null`, which the configurator shows as unchecked,
   * so only a missing value falls back to the default.
   */
  protected readonly isTopbarSticky: Signal<boolean> = computed(() => {
    const sticky = this.configService.config()?.header?.topbar?.sticky;
    return sticky === undefined ? (DEFAULT_CONFIG.header?.topbar?.sticky ?? true) : !!sticky;
  });
  /** The rendered height (in px) of the header host, tracked by a `ResizeObserver`. */
  private readonly measuredHeight: WritableSignal<number> = signal(0);
  /**
   * The `top` offset (in px) for the sticky header, or `null` when the header is not sticky.
   * `position: sticky` requires a `top` value to pin at all, so a sticky header pins at `0`. When the
   * topbar should scroll away, the header is shifted up by the topbar's measured height instead, so the
   * topbar leaves the viewport while the main header stays pinned.
   */
  protected readonly stickyTopOffset: Signal<number | null> = computed(() => {
    if (!this.isSticky()) {
      return null;
    }
    return this.isTopbarSticky() ? 0 : -this.headerService.topbarHeight();
  });

  constructor() {
    // Publishes the height the pinned header covers, so anchored components can offset their scroll target.
    effect(() => {
      if (!this.isSticky()) {
        this.headerService.stickyHeight.set(0);
        return;
      }
      // A topbar that scrolls away is shifted out of the viewport and therefore covers nothing.
      const covered = this.isTopbarSticky() ? this.measuredHeight() : this.measuredHeight() - this.headerService.topbarHeight();
      this.headerService.stickyHeight.set(Math.max(0, covered));
    });

    // ResizeObserver is browser-only; on the server (or when unsupported) leave the height at 0.
    if (!this.system.browser || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      this.measuredHeight.set(Math.round(entries[0]?.contentRect.height ?? 0));
    });
    observer.observe(this.el.nativeElement);

    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      this.headerService.stickyHeight.set(0);
    });
  }
}
